/**
 * Loads one submission on its own.
 *
 * The Create Invoice screen sits outside `SubmissionLayout` — the Figma
 * drops the section nav and submission header there — so it can't read
 * `useSubmissionContext()` and has to fetch the detail itself. Same two
 * calls the layout makes, without the header, nav and patch plumbing.
 */
import { useEffect, useState } from "react";
import { fromWireSubmission, getSubmission } from "../../../api";
import type { SubmissionForm } from "../../../types/submission";

export type SubmissionDetailState =
    | { status: "loading" }
    | { status: "ready"; detail: SubmissionForm }
    | { status: "error"; message: string };

export function useSubmissionDetail(submissionId: string): SubmissionDetailState {
    const [state, setState] = useState<SubmissionDetailState>({ status: "loading" });

    useEffect(() => {
        if (!submissionId) {
            setState({ status: "error", message: "No submission id in the URL" });
            return;
        }
        let cancelled = false;
        setState({ status: "loading" });
        getSubmission(submissionId)
            .then((wire) => {
                if (!cancelled) setState({ status: "ready", detail: fromWireSubmission(wire) });
            })
            .catch((e: unknown) => {
                if (!cancelled) {
                    setState({ status: "error", message: e instanceof Error ? e.message : "Unknown error" });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [submissionId]);

    return state;
}
